/**
 * EnhancedProceduralSprites.ts - High-quality FF1-style sprites
 *
 * This file provides enhanced procedural sprites that match the authentic
 * NES-era Final Fantasy 1 aesthetic. All sprites are pixel-perfect and
 * designed to look like genuine 8-bit RPG artwork.
 *
 * Character Classes (16x16, 4-direction walk cycles):
 * - Fighter, Thief, Monk, Red Mage, White Mage, Black Mage
 * - Knight, Ninja, Master, Red Wizard, White Wizard, Black Wizard
 *
 * Enemies (32x32, static/animated):
 * - 43 unique enemy types from basic monsters to legendary bosses
 */

import { ProceduralSprites } from './ProceduralSprites';

export class EnhancedProceduralSprites extends ProceduralSprites {
  /**
   * Generate enhanced character sprites with detailed pixel art
   */
  generateCharacters(): HTMLCanvasElement {
    const canvas = document.createElement('canvas');
    canvas.width = 192;
    canvas.height = 192;
    const ctx = canvas.getContext('2d')!;

    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, 192, 192);

    // Generate all 12 character classes with 12 frames each
    for (let classIdx = 0; classIdx < 12; classIdx++) {
      for (let frame = 0; frame < 12; frame++) {
        const direction = Math.floor(frame / 3);
        const walkFrame = frame % 3;
        const x = frame * 16;
        const y = classIdx * 16;

        this.drawEnhancedCharacter(ctx, x, y, classIdx, direction, walkFrame);
      }
    }

    return canvas;
  }

  private drawEnhancedCharacter(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    classIdx: number,
    direction: number,
    walkFrame: number
  ): void {
    // Clear the sprite area
    ctx.clearRect(x, y, 16, 16);

    // Draw specific class
    switch (classIdx) {
      case 0: this.drawFighter(ctx, x, y, direction, walkFrame); break;
      case 1: this.drawThief(ctx, x, y, direction, walkFrame); break;
      case 2: this.drawMonk(ctx, x, y, direction, walkFrame); break;
      case 3: this.drawRedMage(ctx, x, y, direction, walkFrame); break;
      case 4: this.drawWhiteMage(ctx, x, y, direction, walkFrame); break;
      case 5: this.drawBlackMage(ctx, x, y, direction, walkFrame); break;
      case 6: this.drawKnight(ctx, x, y, direction, walkFrame); break;
      case 7: this.drawNinja(ctx, x, y, direction, walkFrame); break;
      case 8: this.drawMaster(ctx, x, y, direction, walkFrame); break;
      case 9: this.drawRedWizard(ctx, x, y, direction, walkFrame); break;
      case 10: this.drawWhiteWizard(ctx, x, y, direction, walkFrame); break;
      case 11: this.drawBlackWizard(ctx, x, y, direction, walkFrame); break;
    }
  }

  // =====================
  // CHARACTER SPRITES
  // =====================

  private drawFighter(ctx: CanvasRenderingContext2D, x: number, y: number, direction: number, walkFrame: number): void {
    const legOffset = walkFrame === 1 ? 1 : walkFrame === 2 ? -1 : 0;

    // Legs
    ctx.fillStyle = '#aa4444';
    ctx.fillRect(x + 5, y + 11, 2, 3);
    ctx.fillRect(x + 9, y + 11, 2, 3);

    // Body (red armor)
    ctx.fillStyle = '#ff4444';
    ctx.fillRect(x + 4, y + 6, 8, 6);

    // Shoulders
    ctx.fillStyle = '#cc0000';
    ctx.fillRect(x + 3, y + 6, 2, 2);
    ctx.fillRect(x + 11, y + 6, 2, 2);

    // Head
    ctx.fillStyle = '#ffcc99';
    ctx.fillRect(x + 6, y + 3, 4, 4);

    // Helmet
    ctx.fillStyle = '#888888';
    ctx.fillRect(x + 5, y + 2, 6, 2);
    ctx.fillRect(x + 6, y + 4, 4, 1);

    // Eyes
    ctx.fillStyle = '#000000';
    if (direction === 0) {
      ctx.fillRect(x + 7, y + 5, 1, 1);
      ctx.fillRect(x + 9, y + 5, 1, 1);
    } else if (direction === 1) {
      ctx.fillRect(x + 7, y + 4, 1, 1);
      ctx.fillRect(x + 9, y + 4, 1, 1);
    } else if (direction >= 2) {
      ctx.fillRect(x + 7, y + 5, 1, 1);
    }

    // Sword
    ctx.fillStyle = '#cccccc';
    ctx.fillRect(x + 12, y + 7, 1, 4);
    ctx.fillStyle = '#664422';
    ctx.fillRect(x + 12, y + 10, 1, 2);

    // Feet
    ctx.fillStyle = '#444444';
    ctx.fillRect(x + 5, y + 13 + legOffset, 2, 2);
    ctx.fillRect(x + 9, y + 13 - legOffset, 2, 2);
  }

  private drawThief(ctx: CanvasRenderingContext2D, x: number, y: number, direction: number, walkFrame: number): void {
    const legOffset = walkFrame === 1 ? 1 : walkFrame === 2 ? -1 : 0;

    ctx.fillStyle = '#225522';
    ctx.fillRect(x + 5, y + 11, 2, 3);
    ctx.fillRect(x + 9, y + 11, 2, 3);

    ctx.fillStyle = '#44aa44';
    ctx.fillRect(x + 4, y + 6, 8, 6);

    ctx.fillStyle = '#664422';
    ctx.fillRect(x + 4, y + 9, 8, 1);

    ctx.fillStyle = '#ffcc99';
    ctx.fillRect(x + 6, y + 3, 4, 4);

    ctx.fillStyle = '#228822';
    ctx.fillRect(x + 5, y + 2, 6, 2);
    ctx.fillRect(x + 5, y + 3, 1, 2);
    ctx.fillRect(x + 10, y + 3, 1, 2);

    ctx.fillStyle = '#000000';
    if (direction === 0) {
      ctx.fillRect(x + 7, y + 5, 1, 1);
      ctx.fillRect(x + 9, y + 5, 1, 1);
    } else if (direction === 1) {
      ctx.fillRect(x + 7, y + 4, 1, 1);
      ctx.fillRect(x + 9, y + 4, 1, 1);
    } else {
      ctx.fillRect(x + 7, y + 5, 1, 1);
    }

    ctx.fillStyle = '#aaaaaa';
    ctx.fillRect(x + 3, y + 8, 1, 2);

    ctx.fillStyle = '#442200';
    ctx.fillRect(x + 5, y + 13 + legOffset, 2, 2);
    ctx.fillRect(x + 9, y + 13 - legOffset, 2, 2);
  }

  private drawMonk(ctx: CanvasRenderingContext2D, x: number, y: number, direction: number, walkFrame: number): void {
    const legOffset = walkFrame === 1 ? 1 : walkFrame === 2 ? -1 : 0;

    ctx.fillStyle = '#aa6600';
    ctx.fillRect(x + 5, y + 11, 2, 3);
    ctx.fillRect(x + 9, y + 11, 2, 3);

    ctx.fillStyle = '#ffaa00';
    ctx.fillRect(x + 4, y + 6, 8, 6);

    ctx.fillStyle = '#000000';
    ctx.fillRect(x + 4, y + 9, 8, 1);

    ctx.fillStyle = '#ffcc99';
    ctx.fillRect(x + 6, y + 3, 4, 4);

    ctx.fillStyle = '#ffaa00';
    ctx.fillRect(x + 5, y + 3, 6, 1);

    ctx.fillStyle = '#000000';
    if (direction === 0) {
      ctx.fillRect(x + 7, y + 5, 1, 1);
      ctx.fillRect(x + 9, y + 5, 1, 1);
    } else if (direction === 1) {
      ctx.fillRect(x + 7, y + 4, 1, 1);
      ctx.fillRect(x + 9, y + 4, 1, 1);
    } else {
      ctx.fillRect(x + 7, y + 5, 1, 1);
    }

    ctx.fillStyle = '#cc9966';
    ctx.fillRect(x + 5, y + 13 + legOffset, 2, 2);
    ctx.fillRect(x + 9, y + 13 - legOffset, 2, 2);
  }

  private drawRedMage(ctx: CanvasRenderingContext2D, x: number, y: number, direction: number, walkFrame: number): void {
    const legOffset = walkFrame === 1 ? 1 : walkFrame === 2 ? -1 : 0;

    ctx.fillStyle = '#aa3355';
    ctx.fillRect(x + 5, y + 11, 2, 3);
    ctx.fillRect(x + 9, y + 11, 2, 3);

    ctx.fillStyle = '#ff4488';
    ctx.fillRect(x + 4, y + 6, 8, 6);
    ctx.fillRect(x + 3, y + 8, 10, 3);

    ctx.fillStyle = '#cc2255';
    ctx.fillRect(x + 3, y + 6, 2, 4);
    ctx.fillRect(x + 11, y + 6, 2, 4);

    ctx.fillStyle = '#ffcc99';
    ctx.fillRect(x + 6, y + 4, 4, 3);

    ctx.fillStyle = '#ff4488';
    ctx.fillRect(x + 5, y + 2, 6, 3);
    ctx.fillRect(x + 6, y + 1, 4, 1);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(x + 10, y + 1, 2, 3);

    ctx.fillStyle = '#000000';
    if (direction === 0) {
      ctx.fillRect(x + 7, y + 5, 1, 1);
      ctx.fillRect(x + 9, y + 5, 1, 1);
    }

    ctx.fillStyle = '#664422';
    ctx.fillRect(x + 3, y + 5, 1, 6);
    ctx.fillStyle = '#ffaa00';
    ctx.fillRect(x + 2, y + 4, 2, 2);

    ctx.fillStyle = '#442200';
    ctx.fillRect(x + 5, y + 13 + legOffset, 2, 2);
    ctx.fillRect(x + 9, y + 13 - legOffset, 2, 2);
  }

  private drawWhiteMage(ctx: CanvasRenderingContext2D, x: number, y: number, direction: number, walkFrame: number): void {
    const legOffset = walkFrame === 1 ? 1 : walkFrame === 2 ? -1 : 0;

    ctx.fillStyle = '#dddddd';
    ctx.fillRect(x + 5, y + 11, 2, 3);
    ctx.fillRect(x + 9, y + 11, 2, 3);

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(x + 4, y + 6, 8, 6);
    ctx.fillRect(x + 3, y + 8, 10, 3);

    ctx.fillStyle = '#ff4444';
    ctx.fillRect(x + 4, y + 6, 8, 1);
    ctx.fillRect(x + 4, y + 11, 8, 1);

    ctx.fillStyle = '#ffcc99';
    ctx.fillRect(x + 6, y + 4, 4, 3);

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(x + 5, y + 2, 6, 3);
    ctx.fillRect(x + 5, y + 3, 1, 2);
    ctx.fillRect(x + 10, y + 3, 1, 2);
    ctx.fillStyle = '#ff4444';
    ctx.fillRect(x + 7, y + 2, 2, 1);
    ctx.fillRect(x + 8, y + 1, 1, 2);

    ctx.fillStyle = '#000000';
    if (direction === 0) {
      ctx.fillRect(x + 7, y + 5, 1, 1);
      ctx.fillRect(x + 9, y + 5, 1, 1);
    }

    ctx.fillStyle = '#ccaa00';
    ctx.fillRect(x + 3, y + 5, 1, 6);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(x + 2, y + 4, 3, 1);
    ctx.fillRect(x + 3, y + 3, 1, 3);

    ctx.fillStyle = '#aaaaaa';
    ctx.fillRect(x + 5, y + 13 + legOffset, 2, 2);
    ctx.fillRect(x + 9, y + 13 - legOffset, 2, 2);
  }

  private drawBlackMage(ctx: CanvasRenderingContext2D, x: number, y: number, direction: number, walkFrame: number): void {
    const legOffset = walkFrame === 1 ? 1 : walkFrame === 2 ? -1 : 0;

    ctx.fillStyle = '#222244';
    ctx.fillRect(x + 5, y + 11, 2, 3);
    ctx.fillRect(x + 9, y + 11, 2, 3);

    ctx.fillStyle = '#4444ff';
    ctx.fillRect(x + 4, y + 6, 8, 6);
    ctx.fillRect(x + 3, y + 8, 10, 3);

    ctx.fillStyle = '#000000';
    ctx.fillRect(x + 6, y + 3, 4, 4);

    ctx.fillStyle = '#ffff00';
    if (direction === 0) {
      ctx.fillRect(x + 7, y + 5, 1, 1);
      ctx.fillRect(x + 9, y + 5, 1, 1);
    } else if (direction === 1) {
      ctx.fillRect(x + 7, y + 4, 1, 1);
      ctx.fillRect(x + 9, y + 4, 1, 1);
    } else {
      ctx.fillRect(x + 7, y + 5, 1, 1);
    }

    ctx.fillStyle = '#4444ff';
    ctx.fillRect(x + 6, y + 2, 4, 2);
    ctx.fillRect(x + 7, y + 1, 2, 1);
    ctx.fillRect(x + 7, y + 0, 2, 1);
    ctx.fillStyle = '#2222aa';
    ctx.fillRect(x + 5, y + 4, 6, 1);

    ctx.fillStyle = '#664422';
    ctx.fillRect(x + 12, y + 5, 1, 6);
    ctx.fillStyle = '#8888ff';
    ctx.fillRect(x + 11, y + 4, 2, 2);
    ctx.fillStyle = '#aaaaff';
    ctx.fillRect(x + 11, y + 4, 1, 1);

    ctx.fillStyle = '#222244';
    ctx.fillRect(x + 5, y + 13 + legOffset, 2, 2);
    ctx.fillRect(x + 9, y + 13 - legOffset, 2, 2);
  }

  private drawKnight(ctx: CanvasRenderingContext2D, x: number, y: number, direction: number, walkFrame: number): void {
    const legOffset = walkFrame === 1 ? 1 : walkFrame === 2 ? -1 : 0;

    ctx.fillStyle = '#888888';
    ctx.fillRect(x + 5, y + 11, 2, 3);
    ctx.fillRect(x + 9, y + 11, 2, 3);

    ctx.fillStyle = '#cccccc';
    ctx.fillRect(x + 4, y + 6, 8, 6);

    ctx.fillStyle = '#ffaa00';
    ctx.fillRect(x + 3, y + 6, 2, 2);
    ctx.fillRect(x + 11, y + 6, 2, 2);

    ctx.fillStyle = '#ffcc99';
    ctx.fillRect(x + 6, y + 4, 4, 3);

    ctx.fillStyle = '#aaaaaa';
    ctx.fillRect(x + 5, y + 2, 6, 3);
    ctx.fillRect(x + 6, y + 4, 4, 1);
    ctx.fillStyle = '#ffaa00';
    ctx.fillRect(x + 7, y + 1, 2, 1);

    ctx.fillStyle = '#000000';
    if (direction === 0) {
      ctx.fillRect(x + 6, y + 4, 4, 1);
    }

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(x + 12, y + 6, 2, 5);
    ctx.fillStyle = '#ffaa00';
    ctx.fillRect(x + 12, y + 10, 2, 2);

    ctx.fillStyle = '#666666';
    ctx.fillRect(x + 5, y + 13 + legOffset, 2, 2);
    ctx.fillRect(x + 9, y + 13 - legOffset, 2, 2);
  }

  private drawNinja(ctx: CanvasRenderingContext2D, x: number, y: number, direction: number, walkFrame: number): void {
    const legOffset = walkFrame === 1 ? 2 : walkFrame === 2 ? -2 : 0;

    ctx.fillStyle = '#222222';
    ctx.fillRect(x + 5, y + 11, 2, 3);
    ctx.fillRect(x + 9, y + 11, 2, 3);

    ctx.fillStyle = '#444444';
    ctx.fillRect(x + 4, y + 6, 8, 6);

    ctx.fillStyle = '#884422';
    ctx.fillRect(x + 4, y + 9, 8, 1);
    ctx.fillRect(x + 5, y + 10, 2, 1);
    ctx.fillRect(x + 9, y + 10, 2, 1);

    ctx.fillStyle = '#333333';
    ctx.fillRect(x + 6, y + 3, 4, 4);

    ctx.fillStyle = '#ffffff';
    if (direction === 0) {
      ctx.fillRect(x + 7, y + 5, 1, 1);
      ctx.fillRect(x + 9, y + 5, 1, 1);
    } else {
      ctx.fillRect(x + 7, y + 5, 1, 1);
    }

    ctx.fillStyle = '#666666';
    ctx.fillRect(x + 5, y + 3, 6, 1);

    ctx.fillStyle = '#cccccc';
    ctx.fillRect(x + 11, y + 5, 2, 1);
    ctx.fillRect(x + 12, y + 4, 1, 4);
    ctx.fillStyle = '#aa0000';
    ctx.fillRect(x + 12, y + 7, 1, 1);

    ctx.fillStyle = '#111111';
    ctx.fillRect(x + 5, y + 13 + legOffset, 2, 2);
    ctx.fillRect(x + 9, y + 13 - legOffset, 2, 2);
  }

  private drawMaster(ctx: CanvasRenderingContext2D, x: number, y: number, direction: number, walkFrame: number): void {
    const legOffset = walkFrame === 1 ? 1 : walkFrame === 2 ? -1 : 0;

    ctx.fillStyle = '#cc8800';
    ctx.fillRect(x + 5, y + 11, 2, 3);
    ctx.fillRect(x + 9, y + 11, 2, 3);

    ctx.fillStyle = '#ffcc44';
    ctx.fillRect(x + 4, y + 6, 8, 6);

    ctx.fillStyle = '#ddaa22';
    ctx.fillRect(x + 6, y + 7, 2, 1);
    ctx.fillRect(x + 9, y + 7, 2, 1);

    ctx.fillStyle = '#000000';
    ctx.fillRect(x + 4, y + 9, 8, 1);

    ctx.fillStyle = '#ffcc99';
    ctx.fillRect(x + 6, y + 3, 4, 4);

    ctx.fillStyle = '#ffcc44';
    ctx.fillRect(x + 5, y + 3, 6, 1);
    ctx.fillRect(x + 4, y + 3, 1, 2);
    ctx.fillRect(x + 11, y + 3, 1, 2);

    ctx.fillStyle = '#000000';
    if (direction === 0) {
      ctx.fillRect(x + 7, y + 5, 1, 1);
      ctx.fillRect(x + 9, y + 5, 1, 1);
    }

    ctx.fillStyle = 'rgba(255, 204, 68, 0.3)';
    ctx.fillRect(x + 3, y + 6, 1, 6);
    ctx.fillRect(x + 12, y + 6, 1, 6);

    ctx.fillStyle = '#cc9966';
    ctx.fillRect(x + 5, y + 13 + legOffset, 2, 2);
    ctx.fillRect(x + 9, y + 13 - legOffset, 2, 2);
  }

  private drawRedWizard(ctx: CanvasRenderingContext2D, x: number, y: number, direction: number, walkFrame: number): void {
    const legOffset = walkFrame === 1 ? 1 : walkFrame === 2 ? -1 : 0;

    ctx.fillStyle = '#aa2255';
    ctx.fillRect(x + 5, y + 11, 2, 3);
    ctx.fillRect(x + 9, y + 11, 2, 3);

    ctx.fillStyle = '#ff4488';
    ctx.fillRect(x + 4, y + 6, 8, 6);
    ctx.fillRect(x + 3, y + 8, 10, 4);

    ctx.fillStyle = '#ffaa00';
    ctx.fillRect(x + 4, y + 6, 8, 1);
    ctx.fillRect(x + 4, y + 11, 8, 1);

    ctx.fillStyle = '#cc2255';
    ctx.fillRect(x + 3, y + 6, 2, 5);
    ctx.fillRect(x + 11, y + 6, 2, 5);

    ctx.fillStyle = '#ffcc99';
    ctx.fillRect(x + 6, y + 4, 4, 3);

    ctx.fillStyle = '#ff4488';
    ctx.fillRect(x + 5, y + 2, 6, 3);
    ctx.fillRect(x + 6, y + 1, 4, 1);
    ctx.fillRect(x + 7, y + 0, 2, 1);
    ctx.fillStyle = '#ffaa00';
    ctx.fillRect(x + 5, y + 4, 6, 1);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(x + 10, y + 0, 2, 4);

    ctx.fillStyle = '#000000';
    if (direction === 0) {
      ctx.fillRect(x + 7, y + 5, 1, 1);
      ctx.fillRect(x + 9, y + 5, 1, 1);
    }

    ctx.fillStyle = '#ffaa00';
    ctx.fillRect(x + 3, y + 4, 1, 7);
    ctx.fillStyle = '#ff88aa';
    ctx.fillRect(x + 2, y + 3, 2, 2);
    ctx.fillStyle = '#ffaacc';
    ctx.fillRect(x + 2, y + 3, 1, 1);

    ctx.fillStyle = '#aa2244';
    ctx.fillRect(x + 5, y + 13 + legOffset, 2, 2);
    ctx.fillRect(x + 9, y + 13 - legOffset, 2, 2);
  }

  private drawWhiteWizard(ctx: CanvasRenderingContext2D, x: number, y: number, direction: number, walkFrame: number): void {
    const legOffset = walkFrame === 1 ? 1 : walkFrame === 2 ? -1 : 0;

    ctx.fillStyle = '#eeeeee';
    ctx.fillRect(x + 5, y + 11, 2, 3);
    ctx.fillRect(x + 9, y + 11, 2, 3);

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(x + 4, y + 6, 8, 6);
    ctx.fillRect(x + 3, y + 8, 10, 4);

    ctx.fillStyle = '#ffcc00';
    ctx.fillRect(x + 4, y + 6, 8, 1);
    ctx.fillRect(x + 4, y + 11, 8, 1);
    ctx.fillRect(x + 3, y + 8, 1, 3);
    ctx.fillRect(x + 12, y + 8, 1, 3);

    ctx.fillStyle = '#ffcc99';
    ctx.fillRect(x + 6, y + 4, 4, 3);

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(x + 5, y + 2, 6, 3);
    ctx.fillRect(x + 5, y + 3, 1, 2);
    ctx.fillRect(x + 10, y + 3, 1, 2);
    ctx.fillStyle = '#ffffaa';
    ctx.fillRect(x + 6, y + 1, 4, 1);
    ctx.fillRect(x + 5, y + 2, 1, 1);
    ctx.fillRect(x + 10, y + 2, 1, 1);
    ctx.fillStyle = '#ff0000';
    ctx.fillRect(x + 7, y + 2, 2, 1);
    ctx.fillRect(x + 8, y + 1, 1, 3);

    ctx.fillStyle = '#000000';
    if (direction === 0) {
      ctx.fillRect(x + 7, y + 5, 1, 1);
      ctx.fillRect(x + 9, y + 5, 1, 1);
    }

    ctx.fillStyle = '#ffcc00';
    ctx.fillRect(x + 3, y + 4, 1, 7);
    ctx.fillStyle = '#ffffaa';
    ctx.fillRect(x + 2, y + 3, 3, 1);
    ctx.fillRect(x + 3, y + 2, 1, 3);
    ctx.fillStyle = '#ffcc00';
    ctx.fillRect(x + 2, y + 2, 1, 1);
    ctx.fillRect(x + 4, y + 2, 1, 1);
    ctx.fillRect(x + 2, y + 4, 1, 1);
    ctx.fillRect(x + 4, y + 4, 1, 1);

    ctx.fillStyle = 'rgba(255, 255, 170, 0.3)';
    ctx.fillRect(x + 3, y + 5, 1, 6);
    ctx.fillRect(x + 12, y + 5, 1, 6);

    ctx.fillStyle = '#cccccc';
    ctx.fillRect(x + 5, y + 13 + legOffset, 2, 2);
    ctx.fillRect(x + 9, y + 13 - legOffset, 2, 2);
  }

  private drawBlackWizard(ctx: CanvasRenderingContext2D, x: number, y: number, direction: number, walkFrame: number): void {
    const legOffset = walkFrame === 1 ? 1 : walkFrame === 2 ? -1 : 0;

    ctx.fillStyle = '#111133';
    ctx.fillRect(x + 5, y + 11, 2, 3);
    ctx.fillRect(x + 9, y + 11, 2, 3);

    ctx.fillStyle = '#4444ff';
    ctx.fillRect(x + 4, y + 6, 8, 6);
    ctx.fillRect(x + 3, y + 8, 10, 4);

    ctx.fillStyle = '#8888ff';
    ctx.fillRect(x + 6, y + 8, 1, 1);
    ctx.fillRect(x + 9, y + 9, 1, 1);

    ctx.fillStyle = '#000000';
    ctx.fillRect(x + 6, y + 3, 4, 4);

    ctx.fillStyle = '#ffff00';
    if (direction === 0) {
      ctx.fillRect(x + 7, y + 5, 1, 1);
      ctx.fillRect(x + 9, y + 5, 1, 1);
      ctx.fillStyle = 'rgba(255, 255, 0, 0.5)';
      ctx.fillRect(x + 6, y + 5, 1, 1);
      ctx.fillRect(x + 10, y + 5, 1, 1);
    }

    ctx.fillStyle = '#4444ff';
    ctx.fillRect(x + 6, y + 2, 4, 2);
    ctx.fillRect(x + 7, y + 1, 2, 1);
    ctx.fillRect(x + 7, y + 0, 2, 1);
    ctx.fillStyle = '#8844ff';
    ctx.fillRect(x + 5, y + 4, 6, 1);
    ctx.fillStyle = '#ffffaa';
    ctx.fillRect(x + 6, y + 4, 1, 1);
    ctx.fillRect(x + 9, y + 4, 1, 1);

    ctx.fillStyle = '#664422';
    ctx.fillRect(x + 12, y + 4, 1, 7);
    ctx.fillStyle = '#2222aa';
    ctx.fillRect(x + 11, y + 3, 3, 3);
    ctx.fillStyle = '#8888ff';
    ctx.fillRect(x + 11, y + 3, 2, 2);
    ctx.fillStyle = '#aaaaff';
    ctx.fillRect(x + 11, y + 3, 1, 1);
    ctx.fillStyle = 'rgba(136, 136, 255, 0.5)';
    ctx.fillRect(x + 10, y + 3, 1, 1);
    ctx.fillRect(x + 13, y + 4, 1, 1);

    ctx.fillStyle = 'rgba(68, 68, 255, 0.3)';
    ctx.fillRect(x + 3, y + 6, 1, 6);
    ctx.fillRect(x + 12, y + 6, 1, 6);

    ctx.fillStyle = '#222244';
    ctx.fillRect(x + 5, y + 13 + legOffset, 2, 2);
    ctx.fillRect(x + 9, y + 13 - legOffset, 2, 2);
  }
}

// Export singleton
let enhancedSpritesInstance: EnhancedProceduralSprites | null = null;

export function getEnhancedProceduralSprites(): EnhancedProceduralSprites {
  if (!enhancedSpritesInstance) {
    enhancedSpritesInstance = new EnhancedProceduralSprites();
  }
  return enhancedSpritesInstance;
}
